import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FlightDetails from "./FlightResults/FlightDetails";
import {
  FaPlane,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaChair,
  FaSuitcase,
  FaSpinner,
  FaInfoCircle,
  FaUtensils,
  FaExchangeAlt,
  FaCheckCircle,
  FaWeightHanging,
  FaHamburger,
  FaTag,
  FaIdCard,
  FaFileAlt,
} from "react-icons/fa";
import {
  processFareOptions,
  calculateTotalDuration,
  calculateStopsText,
} from "../utils/flightHelpers";
import { formatTimeAMPM } from "../utils/flightUtils";
import AirlineLogo from "./FlightResults/AirlineLogo";
import { useCurrency } from "../context/CurrencyContext";
import { formatPriceSync } from "../utils/currency";

const OneWayFlightCard = ({
  flight,
  index,
  tripType,
  expandedFlights,
  toggleFlightDetails,
  setSelectedFlightForPrice,
  setShowPriceModal,
  verifyFlightPrice,
  fetchSSROptions,
  handleAuthCheck,
  statusRefdId,
  formatApiDateTime,
  minutesToHm,
  getSegments,
  getStopsText,
  getTotalDurationMinutes,
  isExpanded,
  setIsExpanded,
  expandedOneWayDetails,
  setExpandedOneWayDetails,
  bookWithSSR,
  totalPaxCount = 1,
}) => {
  const { currency } = useCurrency();
  const [fareOptions, setFareOptions] = useState(null);
  const [loadingFares, setLoadingFares] = useState(false);
  const [selectedFare, setSelectedFare] = useState(null);
  const [showFares, setShowFares] = useState(false);
  const [showSSRInfo, setShowSSRInfo] = useState(false);
  const paxCount = Math.max(1, Number(totalPaxCount) || 1);
  const [selectedBaggagePerPax, setSelectedBaggagePerPax] = useState([]);
  const [selectedMealPerPax, setSelectedMealPerPax] = useState([]);

  const resultData = flight?.Flights?.Onward || flight?.data?.result || flight;

  const getSegmentsFunc =
    getSegments ||
    ((leg) => {
      if (!leg) return [];
      if (Array.isArray(leg)) return leg.filter((s) => s && s.flightNo);
      if (typeof leg === "object") {
        const values = Object.values(leg);
        return values.filter((s) => s && s.flightNo);
      }
      return [];
    });

  const flightsData = resultData?.Flights || flight?.Flights;
  const segments = getSegmentsFunc(flightsData?.Onward);
  const firstSegment = segments?.[0] || {};
  const lastSegment = segments?.[segments.length - 1] || {};

  const airlineName =
    firstSegment?.airName || flightsData?.AirlineName || "Airline";
  const flightNo = firstSegment?.flightNo || "";

  const fareData = resultData?.Fare || flight?.Fare;
  const basePrice = parseInt(fareData?.total?.total || 0);

  const fareTypeInd = fareData?.fareTypeInd;
  const fareTypeDesc = fareData?.fareTypeDesc || "";
  const popMsg = fareData?.popMsg || "";
  const refundType = fareData?.refundType || "P";
  const validation = fareData?.validation || {};

  const depTerminal = firstSegment?.depTer || flight?.depTer || "";
  const arrTerminal = lastSegment?.arrTer || flight?.arrTer || "";

  const operatingAirline = firstSegment?.airNameOp || flight?.airNameOp;
  const operatingAirlineCode = firstSegment?.airCodeOp || flight?.airCodeOp;
  const isOperatingDifferent = operatingAirline && operatingAirline !== airlineName;

  const hasFreeMeal = validation?.freeMeal === "1" || validation?.freeMeal === 1;

  const getFareTypeBadge = () => {
    if (!fareTypeDesc) return null;
    const desc = String(fareTypeDesc).toLowerCase();
    if (desc.includes("student")) return { text: "Student", color: "bg-green-100 text-green-800" };
    if (desc.includes("senior")) return { text: "Senior Citizen", color: "bg-purple-100 text-purple-800" };
    if (desc.includes("defence")) return { text: "Defence", color: "bg-blue-100 text-blue-800" };
    if (desc.includes("sme")) return { text: "SME", color: "bg-yellow-100 text-yellow-800" };
    if (desc.includes("promo") || desc.includes("offer")) return { text: "Promo", color: "bg-orange-100 text-orange-800" };
    return null;
  };

  const fareBadge = getFareTypeBadge();

  const handleViewPrices = async () => {
    if (showFares) {
      setShowFares(false);
      setShowSSRInfo(false);
      return;
    }

    setLoadingFares(true);
    try {
      const flightId = firstSegment?.flightID;

      if (!flightId || !statusRefdId) {
        throw new Error("Missing flight ID or reference ID");
      }

      const apiFares = await verifyFlightPrice(flightId, statusRefdId, false, flight);

      console.log("🔍 API Fares received from verifyFlightPrice:", apiFares);
      console.log("🔍 API Fares count:", apiFares?.length);
      console.log("🔍 API Fares is array:", Array.isArray(apiFares));

      if (!apiFares || !Array.isArray(apiFares) || apiFares.length === 0) {
        console.error("❌ No fare options received from API");
        throw new Error("No fare options received from API");
      }

      const processedFares = apiFares.map((fare, index) => {
        // FTD V2: Each fare MUST have flightID from fareDetails – never use search flightID
        if (!fare.flightID) {
          throw new Error(`Fare option ${index + 1} missing flightID from fareDetails`);
        }

        // V2: SSR will be fetched when user selects this fare, not here
        // Parse baggage from API response (Fare.bagCkin and Fare.bagCbin)
        // Format: "15 KG||", "15 Kg (01 Piece only)||", "20 Kg (01 Piece only)||", etc.
        const bagCkin = fare.bagCkin || fare.Fare?.bagCkin || flightsData?.bagCkin || "";
        const bagCbin = fare.bagCbin || fare.Fare?.bagCbin || flightsData?.bagCbin || "";

        // Remove "||" separator and clean up
        const checkinBaggage = bagCkin.replace(/\|\|/g, "").trim();
        const cabinBaggage = bagCbin.replace(/\|\|/g, "").trim();

        // Extract piece count if available (e.g., "15 Kg (01 Piece only)")
        const pieceMatch = checkinBaggage.match(/\((\d+)\s*Piece/i);
        const pieceCount = pieceMatch ? pieceMatch[1] : null;
        const checkinWeight = checkinBaggage.replace(/\s*\(.*?\)/i, "").trim();

        return {
          id: `fare-${index}`,
          name: fare.fareName || fare.fareTypeDesc || `Fare ${index + 1}`,
          price: parseInt(fare.price || basePrice),
          base: parseInt(fare.base || 0),
          tax: parseInt(fare.tax || 0),
          refundable: fare.refundable || (fare.refundType === "P" ? "Partially Refundable" : "Non-Refundable"),
          baggage: checkinBaggage,
          baggageCheckin: checkinWeight,
          baggageCabin: cabinBaggage,
          baggagePieceCount: pieceCount,
          dateChangeFee: fare.dateChangeFee || "Contact Airlines",
          meals: fare.meals === "available" ? "Available" : "Paid",
          seats: fare.seats === "chargeable" ? "Paid" : "Free",
          seatsAvailable: fare.seats || fare.Fare?.seats || "9",
          seatType: fare.seatType || fare.Fare?.fareClass || "SM",
          color: "blue",
          type: "oneway",
          flightData: flight,
          // V2: Store fare-specific flightID and refID for SSR fetch
          flightID: fare.flightID, // From postFareDetails response
          refID: fare.refID, // From postFareDetails Status.refID
          // V2: Store fare type info
          fareTypeInd: fare.fareTypeInd,
          fareTypeDesc: fare.fareTypeDesc,
          popMsg: fare.popMsg,
          ssrInfo: fare.ssrInfo || null,
          baggageOptions: fare.baggageOptions || [],
          mealOptions: fare.mealOptions || [],
          originalFare: fare,
          index: index,
          validation: fare.validation || fare.Validation || fareData?.validation || {},
        };
      });

      console.log("✅ Processed fares count:", processedFares.length);
      console.log("✅ Processed fares:", processedFares);

      if (processedFares.length === 0) {
        console.error("❌ No processed fares available");
        throw new Error("No fare options available");
      }

      // Set fare options and show them
      setFareOptions(processedFares);
      console.log("🔥 STATE UPDATE: setFareOptions called with", processedFares.length, "fares");

      setShowFares(true);
      console.log("🔥 STATE UPDATE: setShowFares(true) called");

      // Clear any previously selected fare when showing new fare options
      setSelectedFare(null);
      console.log("🔥 STATE UPDATE: setSelectedFare(null) called");

      // Force a re-render check
      setTimeout(() => {
        console.log("🔥 TIMEOUT CHECK: showFares should be true, fareOptions should have", processedFares.length, "items");
      }, 100);
    } catch (error) {
      console.error("Error fetching fare options:", error);
      // Parse baggage from flight data
      const defaultBagCkin = flightsData?.bagCkin || fareData?.bagCkin || "";
      const defaultBagCbin = flightsData?.bagCbin || fareData?.bagCbin || "";
      const defaultCheckin = defaultBagCkin.replace(/\|\|/g, "").trim() || "15 KG";
      const defaultCabin = defaultBagCbin.replace(/\|\|/g, "").trim() || "7 KG";
      const defaultPieceMatch = defaultCheckin.match(/\((\d+)\s*Piece/i);
      const defaultPieceCount = defaultPieceMatch ? defaultPieceMatch[1] : null;
      const defaultCheckinWeight = defaultCheckin.replace(/\s*\(.*?\)/i, "").trim();

      const defaultFare = {
        id: "oneway-default",
        name: "Standard Fare",
        price: basePrice,
        base: basePrice,
        tax: 0,
        refundable: "Partially Refundable*",
        baggage: defaultCheckin,
        baggageCheckin: defaultCheckinWeight,
        baggageCabin: defaultCabin,
        baggagePieceCount: defaultPieceCount,
        dateChangeFee: "Contact Airlines",
        meals: "Paid",
        seats: "Paid",
        seatsAvailable: fareData?.seats || "9",
        seatType: fareData?.fareClass || "SM",
        color: "blue",
        type: "oneway",
        flightData: flight,
        ssrInfo: null,
        baggageOptions: [],
        mealOptions: [],
        index: 0,
        validation: fareData?.validation || fareData?.Validation || {},
      };

      setFareOptions([defaultFare]);
      setSelectedFare(defaultFare);
      setShowFares(true);
    } finally {
      setLoadingFares(false);
    }
  };

  const handleSelectFare = async (fare) => {
    if (!fare.flightID) {
      throw new Error("Fare flightID missing – cannot book without fareDetails flightID");
    }
    const flightId = fare.flightID;
    const refId = fare.refID || statusRefdId;
    let baggageOptions = fare.baggageOptions || [];
    let mealOptions = fare.mealOptions || [];
    // V2 FIX: Do NOT call fetchSSROptions here with the fare's flightID.
    // Calling postFareDetails again with the new flightID invalidates the fare!
    // FTD provides SSR info in the initial fare details if available.
    /*
    if (fetchSSROptions && flightId && refId && (baggageOptions.length === 0 && mealOptions.length === 0)) {
      try {
        const ssrData = await fetchSSROptions(flightId, refId, false);
        const onw = ssrData?.ssrInfo?.Onward || {};
        if (onw.Bagg?.length || onw.Meal?.length) {
          baggageOptions = onw.Bagg || [];
          mealOptions = onw.Meal || [];
        }
      } catch (_) {}
    }
    */
    setSelectedFare({
      ...fare,
      flightID: fare.flightID,
      basePrice: fare.price || fare.basePrice || 0,
      priceDifference: 0,
      baggageOptions,
      mealOptions,
    });
    setSelectedBaggagePerPax(Array(paxCount).fill(null));
    setSelectedMealPerPax(Array(paxCount).fill(null));
    setShowSSRInfo((baggageOptions.length > 0 || mealOptions.length > 0));
    setShowFares(false);
  };

  const handleOneWayBookNow = () => {
    if (!selectedFare) {
      alert("Please select a fare to proceed with booking");
      return;
    }

    // FTD: MUST use fare refID from postFareDetails (not search refID) for verify→book chain
    const fareRefID = selectedFare.bookingRefID || selectedFare.refID || statusRefdId;

    // FTD V2: ONLY fareDetails fare.flightID is bookable – search flightID must NEVER be used
    if (!selectedFare.flightID) {
      throw new Error("Fare flightID missing – please select a fare again");
    }
    console.log("SELECTED FARE flightID", selectedFare.flightID, "fareRefID", fareRefID);
    const bookingData = {
      isRoundTrip: false,
      flight: flight,
      selectedFare: selectedFare,
      totalPrice: selectedFare.totalPrice || selectedFare.price,
      flightID: selectedFare.flightID,
      selectedFareFlightID: selectedFare.flightID,
      refID: fareRefID,
      fareRefID,
      flightType: "oneway",
      airlineName: airlineName,
      flightNumber: flightNo,
    };

    // V2: Check if user selected any SSR add-ons (single or per-passenger)
    const hasPerPaxSSR = paxCount > 1 && (selectedBaggagePerPax.some(Boolean) || selectedMealPerPax.some(Boolean));
    const hasSingleSSR = paxCount === 1 && (selectedFare.selectedBaggage || selectedFare.selectedMeal);
    const hasSelectedSSR = hasPerPaxSSR || hasSingleSSR;

    if (hasSelectedSSR) {
      let ssrInfo;
      let selectedBaggagePerPaxOut = null;
      let selectedMealPerPaxOut = null;
      if (hasPerPaxSSR) {
        const bagArray = selectedBaggagePerPax.filter(Boolean).map((b) => ({
          baggID: b.baggID,
          baggAmt: b.baggAmt,
          baggDesc: b.baggDesc,
        }));
        const mealArray = selectedMealPerPax.filter(Boolean).map((m) => ({
          mealID: m.mealID,
          mealAmt: m.mealAmt,
          mealDesc: m.mealDesc,
          mealRef: m.mealRef || 1,
        }));
        ssrInfo = {
          Onward: {
            Bagg: bagArray,
            Meal: mealArray,
          },
        };
        selectedBaggagePerPaxOut = selectedBaggagePerPax;
        selectedMealPerPaxOut = selectedMealPerPax;
      } else {
        ssrInfo = {
          Onward: {
            Bagg: selectedFare.selectedBaggage ? [selectedFare.selectedBaggage] : [],
            Meal: selectedFare.selectedMeal ? [selectedFare.selectedMeal] : [],
          },
        };
      }

      const ssrBookingData = {
        ...bookingData,
        ssrInfo,
        selectedBaggage: selectedFare.selectedBaggage,
        selectedMeal: selectedFare.selectedMeal,
        selectedBaggagePerPax: selectedBaggagePerPaxOut,
        selectedMealPerPax: selectedMealPerPaxOut,
        baggageOptions: selectedFare.baggageOptions || [],
        mealOptions: selectedFare.mealOptions || [],
        hasSSR: true,
        doc_mandatory: selectedFare.doc_mandatory || 0,
        pan_mandatory: selectedFare.pan_mandatory || 0,
        document_type: selectedFare.document_type || 0,
      };

      if (bookWithSSR) {
        bookWithSSR(ssrBookingData);
      } else if (handleAuthCheck) {
        handleAuthCheck(ssrBookingData);
      } else {
        console.error("No booking function available for SSR booking");
        alert("Unable to process booking with add-ons. Please try again.");
      }
    } else {
      const regularBookingData = {
        ...bookingData,
        hasSSR: false,
        ssrInfo: null,
        baggageOptions: [],
        mealOptions: [],
        // V2: Validation requirements
        doc_mandatory: selectedFare.doc_mandatory || 0,
        pan_mandatory: selectedFare.pan_mandatory || 0,
        document_type: selectedFare.document_type || 0,
      };

      if (handleAuthCheck) {
        handleAuthCheck(regularBookingData);
      } else {
        console.error("No booking function available");
        alert("Unable to process booking. Please try again.");
      }
    }
  };

  const flightDuration = minutesToHm
    ? minutesToHm(calculateTotalDuration(flightsData?.Onward))
    : "--h --m";

  const stopsText = calculateStopsText(flightsData?.Onward);

  const departureTime =
    firstSegment?.depDate || firstSegment?.depTime
      ? formatTimeAMPM(firstSegment.depDate, firstSegment.depTime)
      : "--:--";
  const arrivalTime =
    lastSegment?.arrDate || lastSegment?.arrTime
      ? formatTimeAMPM(lastSegment.arrDate, lastSegment.arrTime)
      : "--:--";


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-3 hover:shadow-md transition-all duration-200 max-w-5xl mx-auto"
    >
      {/* Compact Header - Responsive */}
      <div className="bg-white px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
          <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto">
            <AirlineLogo
              airlineCode={firstSegment?.airCode || ""}
              airlineName={airlineName}
              className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <h3 className="text-xs sm:text-sm font-bold text-gray-900 truncate">{airlineName}</h3>
                {fareBadge && (
                  <span className={`px-1.5 py-0.5 text-xs font-semibold rounded flex-shrink-0 ${fareBadge.color}`}>
                    {fareBadge.text}
                  </span>
                )}
                {hasFreeMeal && (
                  <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-800 flex-shrink-0">
                    Free Meal
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 truncate">{flightNo}</p>
            </div>
          </div>
          <div className="text-left sm:text-right w-full sm:w-auto flex items-center justify-between sm:block">
            <div className="text-base sm:text-lg font-bold text-blue-600">
              {formatPriceSync(selectedFare ? selectedFare.price : basePrice, currency)}
            </div>
            {popMsg && (
              <span className="text-xs text-orange-600 font-medium">Sale</span>
            )}
          </div>
        </div>
      </div>

      {/* Flight Route - Compact - Responsive */}
      <div className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="text-center flex-1 min-w-0">
            <div className="text-sm sm:text-base font-bold text-gray-900 truncate">
              {firstSegment?.depCode || "---"}
            </div>
            <div className="text-xs sm:text-sm font-semibold text-gray-700">{departureTime}</div>
            <div className="text-xs text-gray-500 truncate">
              {formatApiDateTime && firstSegment?.depDate
                ? formatApiDateTime(firstSegment.depDate)?.split(",")[0]
                : "26 Jan"}
            </div>
          </div>

          <div className="flex-1 px-2 sm:px-4 min-w-0">
            <div className="relative">
              <div className="flex items-center">
                <div className="flex-1 border-t border-dashed border-gray-400"></div>
                <div className="mx-1 sm:mx-2 relative flex-shrink-0">
                  <FaPlane className="text-blue-500 text-xs" />
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 whitespace-nowrap hidden sm:block">
                    {flightDuration}
                  </div>
                </div>
                <div className="flex-1 border-t border-dashed border-gray-400"></div>
              </div>
              <div className="text-center text-xs text-gray-500 mt-1 truncate">
                <span className="sm:hidden">{flightDuration} • </span>{stopsText}
              </div>
            </div>
          </div>

          <div className="text-center flex-1 min-w-0">
            <div className="text-sm sm:text-base font-bold text-gray-900 truncate">
              {lastSegment?.arrCode || "---"}
            </div>
            <div className="text-xs sm:text-sm font-semibold text-gray-700">{arrivalTime}</div>
            <div className="text-xs text-gray-500 truncate">
              {formatApiDateTime && lastSegment?.arrDate
                ? formatApiDateTime(lastSegment.arrDate)?.split(",")[0]
                : "26 Jan"}
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar - Responsive */}
      <div className="px-3 sm:px-4 py-2 bg-white border-b border-gray-200 flex items-center justify-between gap-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center min-h-[36px] sm:min-h-0"
        >
          <span className="hidden sm:inline">Flight Details</span>
          <span className="sm:hidden">Details</span>
          {isExpanded ? (
            <FaChevronUp className="ml-1" size={10} />
          ) : (
            <FaChevronDown className="ml-1" size={10} />
          )}
        </button>
        <button
          onClick={handleViewPrices}
          className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium min-h-[36px] sm:min-h-0 flex items-center justify-center"
        >
          {loadingFares ? (
            <FaSpinner className="animate-spin" />
          ) : showFares ? (
            <span className="hidden sm:inline">Hide Fares</span>
          ) : (
            <span className="hidden sm:inline">View Fares</span>
          )}
          {!loadingFares && !showFares && <span className="sm:hidden">Fares</span>}
          {!loadingFares && showFares && <span className="sm:hidden">Hide</span>}
        </button>
      </div>

      {/* Flight Details Expandable */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <FlightDetails
                flight={flight}
                segments={segments}
                formatApiDateTime={formatApiDateTime}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fare Options Table */}
      <AnimatePresence>
        {showFares && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-200 bg-white">
              {/* Table Header - V2: Base Fare Types - Hidden on mobile, shown on md+ */}
              <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-gradient-to-r from-blue-50 to-purple-50 text-xs font-bold text-gray-800 border-b-2 border-gray-300">
                <div className="col-span-3 flex items-center">
                  <FaTag className="mr-2 text-blue-600" />
                  Base Fare Type
                </div>
                <div className="col-span-2 flex items-center">
                  <FaSuitcase className="mr-2 text-blue-600" />
                  Included Baggage
                </div>
                <div className="col-span-2 flex items-center">
                  <FaUtensils className="mr-2 text-blue-600" />
                  Included Services
                </div>
                <div className="col-span-5 flex items-center justify-between">
                  <span className="flex items-center">
                    <span className="mr-1 text-blue-600 font-semibold">₹</span>
                    Base Price / Select
                  </span>
                </div>
              </div>

              {loadingFares ? (
                <div className="text-center py-8">
                  <FaSpinner className="animate-spin text-blue-500 text-xl mx-auto" />
                  <p className="text-gray-500 mt-2 text-xs">Loading fare options...</p>
                </div>
              ) : fareOptions && fareOptions.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {fareOptions.map((fare, idx) => {
                    const isSelected = selectedFare?.id === fare.id;
                    return (
                      <div
                        key={fare.id || idx}
                        className={`md:grid md:grid-cols-12 md:gap-2 px-3 sm:px-4 py-3 sm:py-4 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${isSelected ? "bg-blue-50 border-l-4 border-blue-500 md:border-l-0 md:border-l-4" : ""
                          }`}
                      >
                        {/* Mobile Layout - Stacked */}
                        <div className="md:hidden space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                <span className="text-sm font-semibold text-gray-900 truncate">
                                  {fare.name || fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || `Fare ${idx + 1}`}
                                </span>
                                {(fare.fareTypeDesc || fare.originalFare?.fareTypeDesc) && (
                                  <span className={`px-1.5 py-0.5 text-xs rounded font-medium flex-shrink-0 ${(fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("sme") ? "bg-yellow-100 text-yellow-800" :
                                      (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("retail") ? "bg-blue-100 text-blue-800" :
                                        (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("special") ? "bg-purple-100 text-purple-800" :
                                          (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("flexi") ? "bg-green-100 text-green-800" :
                                            (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("hand baggage") ? "bg-orange-100 text-orange-800" :
                                              (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("instant offer") ? "bg-pink-100 text-pink-800" :
                                                (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("business") ? "bg-indigo-100 text-indigo-800" :
                                                  "bg-gray-100 text-gray-800"
                                    }`}>
                                    {fare.fareTypeDesc || fare.originalFare?.fareTypeDesc}
                                  </span>
                                )}
                              </div>
                              <div className="text-lg font-bold text-gray-900 mb-1">
                                {formatPriceSync(fare.price, currency)}
                              </div>
                              <div className="text-xs text-gray-500">Total Base Fare</div>
                            </div>
                            <button
                              onClick={() => handleSelectFare(fare)}
                              className={`px-3 py-2 rounded text-xs font-semibold transition-all flex-shrink-0 ${isSelected
                                  ? "bg-green-600 text-white hover:bg-green-700"
                                  : "bg-blue-600 text-white hover:bg-blue-700"
                                }`}
                            >
                              {isSelected ? "Selected" : "Select"}
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                              <div className="font-semibold text-gray-700 mb-1">Baggage</div>
                              <div className="text-gray-600">
                                {fare.baggageCheckin || fare.baggage || "15 KG"}
                                {fare.baggagePieceCount && ` (${fare.baggagePieceCount} Piece)`}
                              </div>
                              {fare.baggageCabin && (
                                <div className="text-gray-500 text-xs mt-0.5">Cabin: {fare.baggageCabin}</div>
                              )}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-700 mb-1">Services</div>
                              <div className="space-y-0.5 text-gray-600">
                                <div>Seats: {fare.seatsAvailable || "9"} {fare.seatType || "SM"}</div>
                                <div className={fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" || fare.validation?.freeMeal === "1" ? "text-green-600" : "text-gray-600"}>
                                  Meal: {fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" || fare.validation?.freeMeal === "1" ? "Free" : "Paid"}
                                </div>
                                {fare.validation?.gstInd !== undefined && (
                                  <div className={fare.validation.gstInd === "1" ? "text-amber-700 font-medium" : fare.validation.gstInd === "2" ? "text-blue-600" : "text-gray-600"}>
                                    GST: {fare.validation.gstInd === "1" ? "Mandatory" : fare.validation.gstInd === "2" ? "Optional" : "Not Allowed"}
                                  </div>
                                )}
                                {fare.validation?.allowFQT !== undefined && (
                                  <div className="text-gray-600">FQT: {fare.validation.allowFQT === "1" ? "Allowed" : "Not Allowed"}</div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-xs">
                            <span className={fare.refundable?.includes("Non") || fare.originalFare?.refundType === "N" ? "text-red-600 font-medium" : "text-green-600 font-medium"}>
                              {fare.refundable?.includes("Non") || fare.originalFare?.refundType === "N" ? "Non" : "Partially"} Refundable
                            </span>
                            {fare.popMsg && (
                              <div className="text-orange-600 italic mt-1">{fare.popMsg}</div>
                            )}
                          </div>
                        </div>

                        {/* Desktop Layout - Grid */}
                        {/* Fare Rules Column - V2 Enhanced */}
                        <div className="hidden md:block col-span-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-semibold text-gray-900">
                              {fare.name || fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || `Fare ${idx + 1}`}
                            </span>
                            {(fare.fareTypeDesc || fare.originalFare?.fareTypeDesc) && (
                              <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${(fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("sme") ? "bg-yellow-100 text-yellow-800" :
                                  (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("retail") ? "bg-blue-100 text-blue-800" :
                                    (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("special") ? "bg-purple-100 text-purple-800" :
                                      (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("flexi") ? "bg-green-100 text-green-800" :
                                        (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("hand baggage") ? "bg-orange-100 text-orange-800" :
                                          (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("instant offer") ? "bg-pink-100 text-pink-800" :
                                            (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("business") ? "bg-indigo-100 text-indigo-800" :
                                              "bg-gray-100 text-gray-800"
                                }`}>
                                {fare.fareTypeDesc || fare.originalFare?.fareTypeDesc}
                              </span>
                            )}
                          </div>
                          {/* V2: Show fare type index and pop message if available */}
                          {fare.fareTypeInd && (
                            <div className="text-xs text-gray-500 mb-1">
                              Type: {fare.fareTypeInd === "0" ? "Hand Baggage" :
                                fare.fareTypeInd === "1" ? "Instant Offer" :
                                  fare.fareTypeInd === "2" ? "Retail" :
                                    fare.fareTypeInd === "3" ? "SME" :
                                      fare.fareTypeInd === "4" ? "Flexi" :
                                        fare.fareTypeInd === "5" ? "Special C" :
                                          fare.fareTypeInd}
                            </div>
                          )}
                          {fare.popMsg && (
                            <div className="text-xs text-orange-600 mb-1 italic">
                              {fare.popMsg}
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-medium ${fare.refundable?.includes("Non") || fare.originalFare?.refundType === "N" ? "text-red-600" : "text-green-600"
                              }`}>
                              {fare.refundable?.includes("Non") || fare.originalFare?.refundType === "N" ? "Non" : "Partially"} Refundable
                            </span>
                          </div>
                        </div>

                        {/* Baggage Column */}
                        <div className="hidden md:block col-span-2">
                          <div className="text-xs text-gray-700 space-y-1">
                            <div className="font-medium">
                              {fare.baggageCheckin || fare.baggage || "15 KG"}
                              {fare.baggagePieceCount && (
                                <span className="text-gray-600 font-normal">
                                  {" "}({fare.baggagePieceCount} Piece only)
                                </span>
                              )}
                            </div>
                            {fare.baggageCabin && (
                              <div className="text-gray-600 text-xs">
                                Cabin: {fare.baggageCabin}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Meal, Validation (GST, FQT) Column – Included Services */}
                        <div className="hidden md:block col-span-2">
                          <div className="space-y-1 text-xs">
                            <div className="text-gray-700">
                              <span className="font-medium"># Seats –</span> {fare.seatsAvailable || "9"} {fare.seatType || "SM"}
                            </div>
                            <div className={`flex items-center ${fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" || fare.validation?.freeMeal === "1"
                                ? "text-green-600"
                                : "text-gray-600"
                              }`}>
                              <FaUtensils className="mr-1" size={10} />
                              <span>Meal – {fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" || fare.validation?.freeMeal === "1" ? "Free" : "Paid"}</span>
                            </div>
                            {fare.validation?.gstInd !== undefined && (
                              <div className={`font-medium ${fare.validation.gstInd === "1" ? "text-amber-700" : fare.validation.gstInd === "2" ? "text-blue-600" : "text-gray-600"
                                }`}>
                                GST – {fare.validation.gstInd === "1" ? "Mandatory" : fare.validation.gstInd === "2" ? "Optional" : "Not Allowed"}
                              </div>
                            )}
                            {fare.validation?.allowFQT !== undefined && (
                              <div className="text-gray-600">
                                FQT – {fare.validation.allowFQT === "1" ? "Allowed" : "Not Allowed"}
                              </div>
                            )}
                            <div className="text-gray-600">
                              <FaChair className="mr-1 inline" size={10} />
                              Seat – {fare.seats === "Free" ? "Free" : "Paid"}
                            </div>
                          </div>
                        </div>

                        {/* Price/Actions Column */}
                        <div className="hidden md:flex col-span-5 items-center justify-between">
                          <div>
                            <div className="text-lg font-bold text-gray-900">
                              {formatPriceSync(fare.price, currency)}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              Total Base Fare
                            </div>
                          </div>
                          <button
                            onClick={() => handleSelectFare(fare)}
                            className={`px-4 py-2 rounded text-xs font-semibold transition-all ${isSelected
                                ? "bg-green-600 text-white hover:bg-green-700"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                              }`}
                          >
                            {isSelected ? "Selected" : "Select Base Fare"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No fare options available</p>
                  <p className="text-gray-400 text-xs mt-1">Please try again or contact support</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selected Fare with SSR Add-ons (V2) - Refactored for Clear Separation */}
      {selectedFare && !showFares && (
        <div className="border-t border-gray-300 bg-gradient-to-br from-gray-50 to-white">
          <div className="px-4 py-4 space-y-4">
            {/* BASE FARE SECTION - Mandatory Ticket Price */}
            <div className="bg-white rounded-lg border-2 border-blue-500 p-4 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-gray-700">Base Fare:</span>
                    <span className={`px-2 py-0.5 text-xs rounded font-medium ${selectedFare.fareTypeDesc?.toLowerCase().includes("sme") ? "bg-yellow-100 text-yellow-800" :
                        selectedFare.fareTypeDesc?.toLowerCase().includes("flexi") ? "bg-green-100 text-green-800" :
                          selectedFare.fareTypeDesc?.toLowerCase().includes("special") ? "bg-purple-100 text-purple-800" :
                            "bg-blue-100 text-blue-800"
                      }`}>
                      {selectedFare.fareTypeDesc || selectedFare.name || "Standard Fare"}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatPriceSync(selectedFare.basePrice || selectedFare.price || 0, currency)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    per person • Mandatory ticket price
                  </div>
                  {/* Helper Text */}
                  <div className="mt-2 text-xs text-gray-600 bg-blue-50 p-2 rounded border border-blue-100">
                    <FaInfoCircle className="inline mr-1 text-blue-600" />
                    Base fare includes standard baggage and seat to fly.
                  </div>
                  {/* V2: Show price difference if any */}
                  {selectedFare.priceDifference && selectedFare.priceDifference !== 0 && (
                    <div className={`text-xs mt-2 font-medium ${selectedFare.priceDifference > 0 ? "text-orange-600" : "text-green-600"
                      }`}>
                      {selectedFare.priceDifference > 0 ? "+" : ""}{formatPriceSync(Math.abs(selectedFare.priceDifference), currency)} price adjustment
                    </div>
                  )}
                  {selectedFare.popMsg && (
                    <div className="text-xs text-orange-600 mt-2 italic">
                      {selectedFare.popMsg}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* SSR ADD-ONS SECTION - Optional (per passenger when paxCount > 1) */}
            {(selectedFare.baggageOptions?.length > 0 || selectedFare.mealOptions?.length > 0) && (
              <div className="bg-white rounded-lg border border-gray-300 p-4 shadow-sm">
                <div className="mb-3">
                  <h4 className="text-sm font-bold text-gray-900 mb-1">
                    Add-ons (Optional – Pay only if selected)
                  </h4>
                  <p className="text-xs text-gray-600">
                    {paxCount > 1 ? "Select baggage and meal for each passenger." : "Enhance your journey with optional services."}
                  </p>
                </div>

                <div className="space-y-4">
                  {paxCount > 1 ? (
                    Array.from({ length: paxCount }, (_, paxIdx) => (
                      <div key={paxIdx} className="p-3 rounded-lg border border-gray-200 bg-gray-50/50 space-y-3">
                        <h5 className="text-sm font-semibold text-gray-800">Passenger {paxIdx + 1}</h5>
                        {selectedFare.baggageOptions?.length > 0 && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              <FaSuitcase className="inline mr-1" /> Baggage
                            </label>
                            <select
                              className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                              value={selectedBaggagePerPax[paxIdx]?.baggID || ""}
                              onChange={(e) => {
                                const next = [...selectedBaggagePerPax];
                                if (e.target.value === "") {
                                  next[paxIdx] = null;
                                } else {
                                  const bag = selectedFare.baggageOptions.find((b) => b.baggID === e.target.value);
                                  if (bag) next[paxIdx] = bag;
                                }
                                setSelectedBaggagePerPax(next);
                              }}
                            >
                              <option value="">No extra baggage ({formatPriceSync(0, currency)})</option>
                              {selectedFare.baggageOptions.map((bag, idx) => (
                                <option key={idx} value={bag.baggID}>
                                  {bag.baggDesc} (+{formatPriceSync(parseInt(bag.baggAmt || 0), currency)})
                                </option>
                              ))}
                            </select>
                            {selectedBaggagePerPax[paxIdx] && (
                              <div className="mt-1 text-xs text-green-600">
                                +{formatPriceSync(parseInt(selectedBaggagePerPax[paxIdx].baggAmt || 0), currency)}
                              </div>
                            )}
                          </div>
                        )}
                        {selectedFare.mealOptions?.length > 0 && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              <FaUtensils className="inline mr-1" /> Meal
                            </label>
                            <select
                              className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                              value={selectedMealPerPax[paxIdx]?.mealID || ""}
                              onChange={(e) => {
                                const next = [...selectedMealPerPax];
                                if (e.target.value === "") {
                                  next[paxIdx] = null;
                                } else {
                                  const meal = selectedFare.mealOptions.find((m) => m.mealID === e.target.value);
                                  if (meal) next[paxIdx] = meal;
                                }
                                setSelectedMealPerPax(next);
                              }}
                            >
                              <option value="">No meal ({formatPriceSync(0, currency)})</option>
                              {selectedFare.mealOptions.map((meal, idx) => (
                                <option key={idx} value={meal.mealID}>
                                  {meal.mealDesc} {meal.mealRef && `(Seg ${meal.mealRef})`} (+{formatPriceSync(parseFloat(meal.mealAmt || 0), currency)})
                                </option>
                              ))}
                            </select>
                            {selectedMealPerPax[paxIdx] && (
                              <div className="mt-1 text-xs text-green-600">
                                +{formatPriceSync(parseFloat(selectedMealPerPax[paxIdx].mealAmt || 0), currency)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <>
                      {selectedFare.baggageOptions?.length > 0 && (
                        <div>
                          <label htmlFor="baggage-select" className="block text-sm font-semibold text-gray-700 mb-2">
                            <FaSuitcase className="inline mr-1 text-gray-600" /> Extra Baggage
                          </label>
                          <select
                            id="baggage-select"
                            className="w-full text-sm border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-gray-400 transition-colors"
                            value={selectedFare.selectedBaggage?.baggID || ""}
                            onChange={(e) => {
                              if (e.target.value === "") {
                                setSelectedFare({ ...selectedFare, selectedBaggage: null });
                              } else {
                                const selectedBag = selectedFare.baggageOptions.find((b) => b.baggID === e.target.value);
                                if (selectedBag) setSelectedFare({ ...selectedFare, selectedBaggage: selectedBag });
                              }
                            }}
                          >
                            <option value="">No extra baggage ({formatPriceSync(0, currency)})</option>
                            {selectedFare.baggageOptions.map((bag, idx) => (
                              <option key={idx} value={bag.baggID}>
                                {bag.baggDesc} (+{formatPriceSync(parseInt(bag.baggAmt || 0), currency)})
                              </option>
                            ))}
                          </select>
                          {selectedFare.selectedBaggage && (
                            <div className="mt-2 text-xs text-green-600 font-medium">
                              ✓ Selected: {selectedFare.selectedBaggage.baggDesc} (+{formatPriceSync(parseInt(selectedFare.selectedBaggage.baggAmt || 0), currency)})
                            </div>
                          )}
                        </div>
                      )}
                      {selectedFare.mealOptions?.length > 0 && (
                        <div>
                          <label htmlFor="meal-select" className="block text-sm font-semibold text-gray-700 mb-2">
                            <FaUtensils className="inline mr-1 text-gray-600" /> Meals
                          </label>
                          <select
                            id="meal-select"
                            className="w-full text-sm border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-gray-400 transition-colors"
                            value={selectedFare.selectedMeal?.mealID || ""}
                            onChange={(e) => {
                              if (e.target.value === "") {
                                setSelectedFare({ ...selectedFare, selectedMeal: null });
                              } else {
                                const selectedMeal = selectedFare.mealOptions.find((m) => m.mealID === e.target.value);
                                if (selectedMeal) setSelectedFare({ ...selectedFare, selectedMeal });
                              }
                            }}
                          >
                            <option value="">No meal ({formatPriceSync(0, currency)})</option>
                            {selectedFare.mealOptions.map((meal, idx) => (
                              <option key={idx} value={meal.mealID}>
                                {meal.mealDesc} {meal.mealRef && `(Segment ${meal.mealRef})`} (+{formatPriceSync(parseFloat(meal.mealAmt || 0), currency)})
                              </option>
                            ))}
                          </select>
                          {selectedFare.selectedMeal && (
                            <div className="mt-2 text-xs text-green-600 font-medium">
                              ✓ Selected: {selectedFare.selectedMeal.mealDesc} {selectedFare.selectedMeal.mealRef && `(Segment ${selectedFare.selectedMeal.mealRef})`} (+{formatPriceSync(parseFloat(selectedFare.selectedMeal.mealAmt || 0), currency)})
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* STICKY PRICE SUMMARY - Dynamic Updates */}
            <div className="sticky bottom-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-4 shadow-lg border-t-4 border-blue-700">
              <div className="flex items-center justify-between text-white">
                <div className="flex-1">
                  <div className="text-xs text-blue-100 mb-1">Price Summary</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-semibold">Base Fare:</span>
                    <span className="text-xl font-bold">{formatPriceSync(selectedFare.basePrice || selectedFare.price || 0, currency)}</span>
                  </div>
                  {(paxCount > 1
                    ? selectedBaggagePerPax.some(Boolean) || selectedMealPerPax.some(Boolean)
                    : (selectedFare.selectedBaggage || selectedFare.selectedMeal)
                  ) && (
                      <div className="mt-2 space-y-1">
                        {paxCount > 1 ? (
                          <>
                            <div className="text-xs text-blue-100 flex justify-between">
                              <span>Add-ons ({paxCount} pax):</span>
                              <span className="font-medium">
                                +{formatPriceSync(
                                  selectedBaggagePerPax.reduce((s, b) => s + Number(b?.baggAmt || 0), 0) +
                                  selectedMealPerPax.reduce((s, m) => s + Number(m?.mealAmt || 0), 0),
                                  currency
                                )}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            {selectedFare.selectedBaggage && (
                              <div className="text-xs text-blue-100 flex justify-between">
                                <span>Extra Baggage:</span>
                                <span className="font-medium">+{formatPriceSync(parseInt(selectedFare.selectedBaggage.baggAmt || 0), currency)}</span>
                              </div>
                            )}
                            {selectedFare.selectedMeal && (
                              <div className="text-xs text-blue-100 flex justify-between">
                                <span>Meal:</span>
                                <span className="font-medium">+{formatPriceSync(parseFloat(selectedFare.selectedMeal.mealAmt || 0), currency)}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                </div>
                <div className="text-right ml-4 pl-4 border-l border-blue-400">
                  <div className="text-xs text-blue-100 mb-1">Final Payable</div>
                  <div className="text-2xl font-bold">
                    {formatPriceSync(
                      (selectedFare.basePrice || selectedFare.price || 0) +
                      (selectedFare.priceDifference || 0) +
                      (paxCount > 1
                        ? selectedBaggagePerPax.reduce((s, b) => s + Number(b?.baggAmt || 0), 0) +
                        selectedMealPerPax.reduce((s, m) => s + Number(m?.mealAmt || 0), 0)
                        : (selectedFare.selectedBaggage ? parseInt(selectedFare.selectedBaggage.baggAmt || 0) : 0) +
                        (selectedFare.selectedMeal ? parseFloat(selectedFare.selectedMeal.mealAmt || 0) : 0)),
                      currency
                    )}
                  </div>
                  <div className="text-xs text-blue-200 mt-1">{paxCount > 1 ? `total (${paxCount} passengers)` : "per person"}</div>
                </div>
              </div>
            </div>

            {/* Book Now Button */}
            <button
              onClick={handleOneWayBookNow}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-bold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
            >
              Proceed to Booking
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default OneWayFlightCard;
